/**
 * Tiny zero-dependency markdown renderer. Supports a deliberately small subset
 * (headings, bold, italic, inline code, links, unordered lists, paragraphs) so
 * the CMS can render markdown bodies without pulling in a new dependency. All
 * text is escaped before any inline formatting is applied, so it is safe to
 * render with dangerouslySetInnerHTML.
 *
 * For 'plaintext' bodies, render with <PlainText> which preserves line breaks
 * and escapes everything.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function inline(text: string): string {
  // Operates on the raw line. We escape first, then apply formatting on the
  // escaped string. Because escapeHtml() neutralizes <, >, &, " and ', the only
  // markup that can appear in the output is the tags we insert below — so this
  // is safe to render with dangerouslySetInnerHTML.
  let out = escapeHtml(text)
  // [label](http(s)://url) — only http/https allowed. Run before other inline
  // rules so URL characters aren't mangled. Label is escaped already.
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a class="text-secondary underline underline-offset-2 hover:text-secondary/80" href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  )
  out = out.replace(
    /`([^`]+)`/g,
    '<code class="rounded bg-surface-2 px-1 py-0.5 text-[0.85em]">$1</code>',
  )
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
  return out
}

function renderMarkdown(src: string): string {
  const lines = src.replace(/\r\n/g, '\n').split('\n')
  const html: string[] = []
  let listOpen = false

  const closeList = () => {
    if (listOpen) {
      html.push('</ul>')
      listOpen = false
    }
  }

  let paragraph: string[] = []
  const flushParagraph = () => {
    if (paragraph.length) {
      html.push(`<p>${inline(paragraph.join(' '))}</p>`)
      paragraph = []
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    const bullet = /^[-*]\s+(.*)$/.exec(line)

    if (heading) {
      flushParagraph()
      closeList()
      const level = heading[1]!.length
      const sizes = ['text-2xl', 'text-xl', 'text-lg', 'text-base']
      html.push(
        `<h${level} class="font-display font-bold ${sizes[level - 1]} mt-6 mb-2">${inline(heading[2]!)}</h${level}>`,
      )
    } else if (bullet) {
      flushParagraph()
      if (!listOpen) {
        html.push('<ul class="my-3 list-disc space-y-1 pl-6">')
        listOpen = true
      }
      html.push(`<li>${inline(bullet[1]!)}</li>`)
    } else if (line === '') {
      flushParagraph()
      closeList()
    } else {
      paragraph.push(line)
    }
  }
  flushParagraph()
  closeList()
  return html.join('\n')
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  return (
    <div
      className={className ?? 'space-y-1 text-sm leading-relaxed text-fg'}
      // eslint-disable-next-line react/no-danger -- input is escaped in inline()/escapeHtml before formatting.
      dangerouslySetInnerHTML={{ __html: renderMarkdown(source) }}
    />
  )
}

export function PlainText({ source, className }: { source: string; className?: string }) {
  return (
    <div className={className ?? 'whitespace-pre-wrap text-sm leading-relaxed text-fg'}>
      {source}
    </div>
  )
}

export function ContentBody({
  format,
  body,
  className,
}: {
  format: 'markdown' | 'plaintext'
  body: string
  className?: string
}) {
  return format === 'markdown' ? (
    <Markdown source={body} className={className} />
  ) : (
    <PlainText source={body} className={className} />
  )
}
